import React, { useState, useEffect } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api, firebase } from 'common';
import { onValue, push, update } from 'firebase/database';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';

import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Grid, 
  Typography, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Checkbox, 
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab,
  Card
} from '@mui/material';

const icons = {
  'paypal': require('../assets/payment-icons/paypal-logo.png').default,
  'braintree': require('../assets/payment-icons/braintree-logo.png').default,
  'stripe': require('../assets/payment-icons/stripe-logo.png').default,
  'paytm': require('../assets/payment-icons/paytm-logo.png').default,
  'payulatam': require('../assets/payment-icons/payulatam-logo.png').default,
  'flutterwave': require('../assets/payment-icons/flutterwave-logo.png').default,
  'paystack': require('../assets/payment-icons/paystack-logo.png').default,
  'securepay': require('../assets/payment-icons/securepay-logo.png').default,
  'payfast': require('../assets/payment-icons/payfast-logo.png').default,
  'liqpay': require('../assets/payment-icons/liqpay-logo.png').default,
  'culqi': require('../assets/payment-icons/culqi-logo.png').default,
  'mercadopago': require('../assets/payment-icons/mercadopago-logo.png').default,
  'squareup': require('../assets/payment-icons/squareup-logo.png').default,
  'wipay': require('../assets/payment-icons/wipay-logo.png').default,
  'test': require('../assets/payment-icons/test-logo.png').default,
  'razorpay': require('../assets/payment-icons/razorpay-logo.png').default,
  'paymongo': require('../assets/payment-icons/paymongo-logo.png').default,
  'skipcash': require('../assets/payment-icons/skipcash-logo.png').default,
};

const Withdraws = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    completeWithdraw,
    fetchUsersOnce
  } = api;
  const dispatch = useDispatch();

  const columns = [
    {
      title: 'ID', field: 'id', editable: 'never',
    },
    {
      title: t('requestDate'), field: 'date', defaultSort: 'desc', render: rowData => rowData.date ? moment(rowData.date).format('lll') : null,
      exportTransformer: (rowData) => new Date(rowData.date).toLocaleDateString() + ' ' + new Date(rowData.date).toLocaleTimeString()
    },
    {
      title: t('driver_name'), field: 'name', editable: 'never',
    },
    {
      title: t('amount'), field: 'amount', editable: 'never',
      render: (rowData) =>
        rowData.amount
          ? settings.swipe_symbol
            ? rowData.amount + " " + settings.symbol
            : settings.symbol + " " + rowData.amount
          : settings.swipe_symbol
            ? "0 " + settings.symbol
            : settings.symbol + " 0",
    },
    {
      title: t('processed'), field: 'processed', type: 'boolean', editable: 'never',
    },
    {
      title: t('processDate'), field: 'processDate', render: rowData => rowData.processDate ? moment(rowData.processDate).format('lll') : null,
    },
    { title: t('bankAccountHolderName') || 'Account Holder Name', field: 'bankAccountHolderName', editable: 'never' },
    { title: t('bankName'), field: 'bankName', editable: 'never' },
    { title: t('bankCode'), field: 'bankCode', editable: 'never' },
    { title: t('bankAccount'), field: 'bankAccount', editable: 'never' },
    { title: t('gateway') || 'Gateway', field: 'gateway', editable: 'never' },
    { title: t('transaction_id') || 'Transaction ID', field: 'transactionId', editable: 'never' }
  ];

  const [data, setData] = useState([]);
  const withdrawdata = useSelector(state => state.withdrawdata);
  const providers = useSelector(state => state.paymentmethods.providers);
  const auth = useSelector(state => state.auth);
  const staticusers = useSelector(state => state.usersdata.staticusers);
  const bookings = useSelector(state => state.bookinglistdata.bookings);

  // Tabs state (0: Pending Payouts, 1: Processed Payouts, 2: Debited Amounts, 3: Cash Rides Ledger)
  const [tab, setTab] = useState(0);

  // Modals state
  const [adjustPoolOpen, setAdjustPoolOpen] = useState(false);
  const [newPoolBalance, setNewPoolBalance] = useState('');
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [collectedAmount, setCollectedAmount] = useState('');
  const [addToPool, setAddToPool] = useState(true);

  const [selectedWithdraw, setSelectedWithdraw] = useState(null);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  const [savedCards, setSavedCards] = useState([]);
  const [selectedAdminCardId, setSelectedAdminCardId] = useState('');
  
  const [adminCardNumber, setAdminCardNumber] = useState('');
  const [adminCardHolder, setAdminCardHolder] = useState('');
  const [adminCardExpiry, setAdminCardExpiry] = useState('');
  const [adminCardCvv, setAdminCardCvv] = useState('');
  const [saveAdminCardCheckbox, setSaveAdminCardCheckbox] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);

  const adminUid = auth.profile ? auth.profile.uid : 'admin';

  const providersList = React.useMemo(() => {
    if (!providers) return [];
    if (Array.isArray(providers)) {
      return providers;
    }
    return Object.keys(providers)
      .filter(key => providers[key] && providers[key].active)
      .map(key => ({
        name: key,
        ...providers[key]
      }));
  }, [providers]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (providersList.length > 0) {
      const payoutGateways = providersList.filter(p => p.name !== 'cash' && p.name !== 'wallet');
      if (payoutGateways.length > 0) {
        setSelectedProvider(payoutGateways[0]);
      }
    }
  }, [providersList]);

  useEffect(() => {
    if (!payoutModalOpen || !adminUid) return;
    const dbRef = firebase.savedCardsRef(adminUid);
    const unsubscribe = onValue(dbRef, (snapshot) => {
      const val = snapshot.val();
      if (val) {
        const arr = Object.keys(val).map(key => ({
          id: key,
          card: val[key].card
        }));
        setSavedCards(arr);
        if (arr.length > 0) {
          setSelectedAdminCardId(arr[0].id);
        }
      } else {
        setSavedCards([]);
        setSelectedAdminCardId('');
      }
    });
    return () => unsubscribe();
  }, [payoutModalOpen, adminUid]);

  const handleProcessPayout = () => {
    if (!selectedProvider) {
      alert("Please select a payment gateway.");
      return;
    }

    if (!selectedAdminCardId || selectedAdminCardId === 'new') {
      if (!adminCardNumber || !adminCardHolder || !adminCardExpiry || !adminCardCvv) {
        alert("Please enter payout card details or select a saved card.");
        return;
      }
    }

    setIsProcessing(true);
    
    setTimeout(async () => {
      try {
        if ((!selectedAdminCardId || selectedAdminCardId === 'new') && saveAdminCardCheckbox) {
          const isVisa = adminCardNumber.replace(/\s/g, '').startsWith('4');
          const isMaster = adminCardNumber.replace(/\s/g, '').startsWith('5');
          const newCard = {
            card: {
              network: isVisa ? 'Visa' : isMaster ? 'Mastercard' : 'Other',
              last4: adminCardNumber.replace(/\s/g, '').slice(-4),
              name: adminCardHolder || 'Payout Card',
              expiry_month: adminCardExpiry.split('/')[0],
              expiry_year: '20' + adminCardExpiry.split('/')[1],
            }
          };
          await push(firebase.savedCardsRef(adminUid), newCard);
        }

        const txnId = 'txn_' + Math.random().toString(36).substr(2, 9);
        const usedCardLast4 = selectedAdminCardId && selectedAdminCardId !== 'new'
          ? (savedCards.find(c => c.id === selectedAdminCardId)?.card?.last4 || '••••')
          : adminCardNumber.slice(-4);

        const txnDetails = {
          gateway: selectedProvider.name,
          transactionId: txnId,
          paymentCard: '•••• •••• •••• ' + usedCardLast4
        };

        // Complete standard payout
        dispatch(completeWithdraw(selectedWithdraw, txnDetails));

        // Decrement from payout pool (Present Amount)
        const currentPool = parseFloat(settings.presentAmount) || 0;
        const newPool = Math.max(0, parseFloat((currentPool - parseFloat(selectedWithdraw.amount)).toFixed(settings.decimal || 2)));
        await update(firebase.settingsRef, { presentAmount: newPool });

        setIsProcessing(false);
        setPayoutModalOpen(false);
        alert("Payout processed successfully! Payout pool decremented.");
      } catch (err) {
        setIsProcessing(false);
        console.error("Payout error: ", err);
        alert("Payout processing failed.");
      }
    }, 2000);
  };

  const handleAdjustPool = async () => {
    const amount = parseFloat(newPoolBalance);
    if (isNaN(amount) || amount < 0) {
      alert("Please enter a valid amount.");
      return;
    }
    try {
      await update(firebase.settingsRef, { presentAmount: amount });
      setAdjustPoolOpen(false);
      setNewPoolBalance('');
      alert("Payout pool adjusted successfully.");
    } catch (error) {
      console.error(error);
      alert("Failed to adjust payout pool.");
    }
  };

  const handleCollectCash = async () => {
    const collectAmt = parseFloat(collectedAmount);
    if (isNaN(collectAmt) || collectAmt <= 0) {
      alert("Please enter a valid collection amount.");
      return;
    }

    try {
      const driverBal = parseFloat(selectedDriver.walletBalance) || 0;
      const newDriverBal = parseFloat((driverBal + collectAmt).toFixed(settings.decimal || 2));
      
      const details = {
        type: 'Credit',
        amount: collectAmt,
        date: new Date().getTime(),
        txRef: 'AdminCollection',
        transaction_id: 'coll_' + Math.random().toString(36).substr(2, 9)
      };

      // 1. Credit driver balance
      const driverUpdate = { walletBalance: newDriverBal };
      
      await update(firebase.singleUserRef(selectedDriver.id), driverUpdate);
      await push(firebase.walletHistoryRef(selectedDriver.id), details);

      // 2. Add to payout pool if checked
      if (addToPool) {
        const currentPool = parseFloat(settings.presentAmount) || 0;
        const newPool = parseFloat((currentPool + collectAmt).toFixed(settings.decimal || 2));
        await update(firebase.settingsRef, { presentAmount: newPool });
      }

      setCollectionModalOpen(false);
      alert("Cash collected successfully! Driver wallet updated.");
      dispatch(fetchUsersOnce());
    } catch (err) {
      console.error(err);
      alert("Failed to process collection.");
    }
  };

  useEffect(() => {
    if (withdrawdata.withdraws) {
      setData(withdrawdata.withdraws);
    } else {
      setData([]);
    }
  }, [withdrawdata.withdraws]);

  const [selectedRow, setSelectedRow] = useState(null);

  // Compute datasets
  const pendingPayouts = data.filter(w => !w.processed);
  const processedPayouts = data.filter(w => w.processed);
  const debitedDrivers = (staticusers || []).filter(u => parseFloat(u.walletBalance) < 0);
  const cashBookings = (bookings || []).filter(b => b.payment_mode === 'cash' && (b.status === 'COMPLETE' || b.status === 'PAID'));

  return (
    withdrawdata.loading ? <CircularLoading /> :
      <div style={{ backgroundColor: 'var(--bg-main)', minHeight: '100vh', padding: '24px' }}>
        
        {/* Payout Pool (Present Amount) Card */}
        <Card style={{
          padding: '20px',
          borderRadius: '16px',
          backgroundColor: 'var(--bg-card)',
          border: 'var(--card-border)',
          boxShadow: 'var(--shadow)',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Typography variant="subtitle2" style={{ color: 'var(--text-secondary)', fontWeight: 'bold' }}>
              ADMIN PAYOUT POOL (PRESENT AMOUNT)
            </Typography>
            <Typography variant="h4" style={{ color: 'var(--text-main)', fontWeight: '800', marginTop: '6px' }}>
              {settings.swipe_symbol 
                ? `${(parseFloat(settings.presentAmount) || 0).toFixed(settings.decimal || 2)} ${settings.symbol}` 
                : `${settings.symbol} ${(parseFloat(settings.presentAmount) || 0).toFixed(settings.decimal || 2)}`}
            </Typography>
          </div>
          <Button 
            variant="contained" 
            sx={{
              background: 'linear-gradient(135deg, #E9AE31 0%, #C58F1F 100%)',
              color: '#000',
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 20px',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #F0C35B 0%, #D8A535 100%)',
              }
            }}
            onClick={() => {
              setNewPoolBalance(settings.presentAmount ? String(settings.presentAmount) : '0');
              setAdjustPoolOpen(true);
            }}
          >
            Adjust Pool Balance
          </Button>
        </Card>

        {/* Tab Navigation */}
        <Tabs 
          value={tab} 
          onChange={(e, val) => setTab(val)}
          style={{ marginBottom: '24px' }}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#E9AE31",
              height: 3,
            },
          }}
        >
          <Tab 
            label="Payout Requests" 
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'bold',
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent !important',
              background: 'none !important',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                color: '#E9AE31',
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&.Mui-selected": {
                color: "#E9AE31",
                textShadow: "0 0 10px rgba(233, 174, 49, 0.3)",
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&:focus": {
                backgroundColor: 'transparent !important',
                background: 'none !important',
              }
            }} 
          />
          <Tab 
            label="Processed Payouts" 
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'bold',
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent !important',
              background: 'none !important',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                color: '#E9AE31',
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&.Mui-selected": {
                color: "#E9AE31",
                textShadow: "0 0 10px rgba(233, 174, 49, 0.3)",
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&:focus": {
                backgroundColor: 'transparent !important',
                background: 'none !important',
              }
            }} 
          />
          <Tab 
            label="Debited Amounts" 
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'bold',
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent !important',
              background: 'none !important',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                color: '#E9AE31',
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&.Mui-selected": {
                color: "#E9AE31",
                textShadow: "0 0 10px rgba(233, 174, 49, 0.3)",
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&:focus": {
                backgroundColor: 'transparent !important',
                background: 'none !important',
              }
            }} 
          />
          <Tab 
            label="Cash Rides Ledger" 
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'bold',
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent !important',
              background: 'none !important',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                color: '#E9AE31',
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&.Mui-selected": {
                color: "#E9AE31",
                textShadow: "0 0 10px rgba(233, 174, 49, 0.3)",
                backgroundColor: 'transparent !important',
                background: 'none !important',
              },
              "&:focus": {
                backgroundColor: 'transparent !important',
                background: 'none !important',
              }
            }} 
          />
        </Tabs>

        {/* Tab 0: Payout Requests */}
        {tab === 0 && (
          <MaterialTable
            title="Pending Withdrawal Requests"
            columns={columns}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
              borderRadius: "20px",
              boxShadow: "var(--shadow)",
              padding: "0",
              backgroundColor: "var(--bg-card)",
              backdropFilter: "blur(12px)",
              border: "var(--card-border)",
              overflow: "hidden"
            }}
            data={pendingPayouts}
            onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
            options={{
              pageSize: 10,
              pageSizeOptions: [10, 15, 20],
              rowStyle: rowData => ({
                backgroundColor: (selectedRow === rowData.tableData.id) ? "var(--table-row-selected)" : "var(--table-row-bg)",
                borderBottom: "var(--table-border)",
                fontSize: "14px",
                color: "var(--text-main)"
              }),
              headerStyle: {
                position: "sticky",
                top: "0px",
                textAlign: "center",
                fontWeight: "700",
                fontFamily: 'Inter, sans-serif',
                background: "var(--table-header-bg)",
                color: "var(--text-light)",
                border: "none",
                height: "56px",
                paddingRight: "20px",
                paddingLeft: "20px",
                borderBottom: "var(--card-border)"
              },
              cellStyle: {
                borderBottom: "var(--table-border)",
                textAlign: "center",
                padding: "14px",
                color: "var(--text-main)"
              },
              actionsColumnIndex: -1,
            }}
            localization={{
              toolbar: {
                searchPlaceholder: (t('search')),
              },
              header: {
                actions: (t('actions'))
              }
            }}
            actions={[
              rowData => ({
                icon: () => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                      minWidth: "130px",
                      padding: "6px 12px",
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: "6px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      color: '#fff',
                      fontSize: "12px",
                      fontWeight: "600"
                    }}
                  >
                    {t("process_withdraw")}
                  </div>
                ),
                tooltip: t('process_withdraw'),
                onClick: (event, rowData) => {
                  setSelectedWithdraw(rowData);
                  setPayoutModalOpen(true);
                  setAdminCardNumber('');
                  setAdminCardHolder('');
                  setAdminCardExpiry('');
                  setAdminCardCvv('');
                  setSaveAdminCardCheckbox(false);
                  if (savedCards.length > 0) {
                    setSelectedAdminCardId(savedCards[0].id);
                  } else {
                    setSelectedAdminCardId('');
                  }
                }
              }),
            ]}
          />
        )}

        {/* Tab 1: Processed Payouts */}
        {tab === 1 && (
          <MaterialTable
            title="Processed Payout History"
            columns={columns}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
              borderRadius: "20px",
              boxShadow: "var(--shadow)",
              padding: "0",
              backgroundColor: "var(--bg-card)",
              backdropFilter: "blur(12px)",
              border: "var(--card-border)",
              overflow: "hidden"
            }}
            data={processedPayouts}
            onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
            options={{
              pageSize: 10,
              pageSizeOptions: [10, 15, 20],
              rowStyle: rowData => ({
                backgroundColor: (selectedRow === rowData.tableData.id) ? "var(--table-row-selected)" : "var(--table-row-bg)",
                borderBottom: "var(--table-border)",
                fontSize: "14px",
                color: "var(--text-main)"
              }),
              headerStyle: {
                position: "sticky",
                top: "0px",
                textAlign: "center",
                fontWeight: "700",
                fontFamily: 'Inter, sans-serif',
                background: "var(--table-header-bg)",
                color: "var(--text-light)",
                border: "none",
                height: "56px",
                paddingRight: "20px",
                paddingLeft: "20px",
                borderBottom: "var(--card-border)"
              },
              cellStyle: {
                borderBottom: "var(--table-border)",
                textAlign: "center",
                padding: "14px",
                color: "var(--text-main)"
              },
              actionsColumnIndex: -1,
            }}
            localization={{
              toolbar: {
                searchPlaceholder: (t('search')),
              }
            }}
          />
        )}

        {/* Tab 2: Debited Amounts */}
        {tab === 2 && (
          <MaterialTable
            title="Drivers with Negative Balances (Owed Commissions)"
            columns={[
              { title: 'ID', field: 'id', editable: 'never' },
              { title: t('name') || 'Name', field: 'firstName', render: rowData => rowData.firstName + ' ' + rowData.lastName },
              { title: t('mobile') || 'Mobile', field: 'mobile' },
              { title: t('email') || 'Email', field: 'email' },
              { 
                title: 'Owed Amount', 
                field: 'walletBalance', 
                render: rowData => {
                  const val = Math.abs(parseFloat(rowData.walletBalance));
                  return settings.swipe_symbol ? `${val.toFixed(settings.decimal || 2)} ${settings.symbol}` : `${settings.symbol} ${val.toFixed(settings.decimal || 2)}`;
                } 
              }
            ]}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
              borderRadius: "20px",
              boxShadow: "var(--shadow)",
              padding: "0",
              backgroundColor: "var(--bg-card)",
              backdropFilter: "blur(12px)",
              border: "var(--card-border)",
              overflow: "hidden"
            }}
            data={debitedDrivers}
            options={{
              pageSize: 10,
              pageSizeOptions: [10, 15, 20],
              rowStyle: () => ({
                borderBottom: "var(--table-border)",
                fontSize: "14px",
                color: "var(--text-main)",
                backgroundColor: "var(--table-row-bg)"
              }),
              headerStyle: {
                position: "sticky",
                top: "0px",
                textAlign: "center",
                fontWeight: "700",
                fontFamily: 'Inter, sans-serif',
                background: "var(--table-header-bg)",
                color: "var(--text-light)",
                border: "none",
                height: "56px",
                paddingRight: "20px",
                paddingLeft: "20px",
                borderBottom: "var(--card-border)"
              },
              cellStyle: {
                borderBottom: "var(--table-border)",
                textAlign: "center",
                padding: "14px",
                color: "var(--text-main)"
              },
              actionsColumnIndex: -1,
            }}
            localization={{
              toolbar: {
                searchPlaceholder: (t('search')),
              },
              header: {
                actions: (t('actions'))
              }
            }}
            actions={[
              rowData => ({
                icon: () => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                      minWidth: "120px",
                      padding: "6px 12px",
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: "6px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      color: '#fff',
                      fontSize: "12px",
                      fontWeight: "600"
                    }}
                  >
                    Collect Cash
                  </div>
                ),
                tooltip: 'Collect Cash',
                onClick: (event, rowData) => {
                  setSelectedDriver(rowData);
                  setCollectedAmount(String(Math.abs(parseFloat(rowData.walletBalance))));
                  setAddToPool(true);
                  setCollectionModalOpen(true);
                }
              }),
            ]}
          />
        )}

        {/* Tab 3: Cash Rides Ledger */}
        {tab === 3 && (
          <MaterialTable
            title="Cash Rides & Commissions Ledger"
            columns={[
              { title: 'Booking ID', field: 'id' },
              { title: t('date') || 'Date', field: 'tripdate', render: rowData => moment(rowData.tripdate).format('lll') },
              { title: t('driver_name') || 'Driver', field: 'driver_name' },
              { title: t('customer_name') || 'Customer', field: 'customer_name' },
              { 
                title: 'Trip Cost', 
                field: 'trip_cost',
                render: rowData => settings.swipe_symbol ? `${parseFloat(rowData.trip_cost).toFixed(settings.decimal || 2)} ${settings.symbol}` : `${settings.symbol} ${parseFloat(rowData.trip_cost).toFixed(settings.decimal || 2)}`
              },
              { 
                title: 'Commission Debited', 
                field: 'commission',
                render: rowData => {
                  const comm = parseFloat(rowData.trip_cost) - parseFloat(rowData.driver_share);
                  return settings.swipe_symbol ? `${comm.toFixed(settings.decimal || 2)} ${settings.symbol}` : `${settings.symbol} ${comm.toFixed(settings.decimal || 2)}`;
                }
              }
            ]}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
              borderRadius: "20px",
              boxShadow: "var(--shadow)",
              padding: "0",
              backgroundColor: "var(--bg-card)",
              backdropFilter: "blur(12px)",
              border: "var(--card-border)",
              overflow: "hidden"
            }}
            data={cashBookings}
            options={{
              pageSize: 10,
              pageSizeOptions: [10, 15, 20],
              rowStyle: () => ({
                borderBottom: "var(--table-border)",
                fontSize: "14px",
                color: "var(--text-main)",
                backgroundColor: "var(--table-row-bg)"
              }),
              headerStyle: {
                position: "sticky",
                top: "0px",
                textAlign: "center",
                fontWeight: "700",
                fontFamily: 'Inter, sans-serif',
                background: "var(--table-header-bg)",
                color: "var(--text-light)",
                border: "none",
                height: "56px",
                paddingRight: "20px",
                paddingLeft: "20px",
                borderBottom: "var(--card-border)"
              },
              cellStyle: {
                borderBottom: "var(--table-border)",
                textAlign: "center",
                padding: "14px",
                color: "var(--text-main)"
              }
            }}
            localization={{
              toolbar: {
                searchPlaceholder: (t('search')),
              }
            }}
          />
        )}

        {/* Dialog: Process Standard Payout */}
        <Dialog 
          open={payoutModalOpen} 
          onClose={() => !isProcessing && setPayoutModalOpen(false)}
          PaperProps={{
            style: {
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-main)',
              border: 'var(--card-border)',
              borderRadius: '16px',
              padding: '16px',
              maxWidth: '650px',
              width: '100%',
            }
          }}
        >
          <DialogTitle style={{ color: 'var(--text-main)', fontWeight: 'bold', borderBottom: '1px solid var(--card-border)', paddingBottom: 12 }}>
            {t('process_withdraw') || 'Process Payout'}
          </DialogTitle>
          <DialogContent style={{ marginTop: 15, paddingBottom: 15 }}>
            {selectedWithdraw && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>
                    {t('driver_name')}: {selectedWithdraw.name}
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>
                    {t('amount')}: {settings.swipe_symbol ? `${selectedWithdraw.amount} ${settings.symbol}` : `${settings.symbol} ${selectedWithdraw.amount}`}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <div style={{ backgroundColor: 'var(--table-input-bg)', padding: 16, borderRadius: 12, border: '1px solid var(--card-border)' }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 12, color: 'var(--text-main)', fontSize: 15 }}>
                      Payout Destination Bank Details:
                    </Typography>
                    {selectedWithdraw.bankAccount ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('bankAccountHolderName') || 'Account Holder Name'}
                          </Typography>
                          <Typography variant="body1" style={{ color: 'var(--text-main)', fontWeight: '500' }}>
                            {selectedWithdraw.bankAccountHolderName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('bankName') || 'Bank Name'}
                          </Typography>
                          <Typography variant="body1" style={{ color: 'var(--text-main)', fontWeight: '500' }}>
                            {selectedWithdraw.bankName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('bankAccount') || 'Account Number'}
                          </Typography>
                          <Typography variant="body1" style={{ color: 'var(--text-main)', fontWeight: '500', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                            {selectedWithdraw.bankAccount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('bankCode') || 'IFSC Code'}
                          </Typography>
                          <Typography variant="body1" style={{ color: 'var(--text-main)', fontWeight: '500', fontFamily: 'monospace' }}>
                            {selectedWithdraw.bankCode}
                          </Typography>
                        </Grid>
                      </Grid>
                    ) : selectedWithdraw.cardNumber ? (
                      <div>
                        <Typography variant="body2" style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Card Details (Legacy)
                        </Typography>
                        <Typography variant="body1" style={{ color: 'var(--text-main)', fontWeight: '500' }}>
                          •••• •••• •••• {selectedWithdraw.cardNumber.slice(-4)} ({selectedWithdraw.cardHolder}) Exp: {selectedWithdraw.cardExpiry}
                        </Typography>
                      </div>
                    ) : (
                      <Typography variant="body1" style={{ color: 'var(--text-main)' }}>
                        No destination bank or card details provided.
                      </Typography>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} style={{ marginTop: 10 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8, color: 'var(--text-main)' }}>
                    1. Select Payment Gateway
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel style={{ color: 'var(--text-main)' }}>{t('payment')}</InputLabel>
                    <Select
                      value={selectedProvider ? providersList.findIndex(p => p.name === selectedProvider.name) : ''}
                      label={t('payment')}
                      onChange={(e) => {
                        const idx = parseInt(e.target.value);
                        setSelectedProvider(providersList[idx]);
                      }}
                      style={{ color: 'var(--text-main)' }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            backgroundColor: 'var(--bg-card)',
                            color: 'var(--text-main)',
                          }
                        }
                      }}
                    >
                      {providersList.filter(p => p.name !== 'cash' && p.name !== 'wallet').map((provider, index) => (
                        <MenuItem key={provider.name} value={providersList.findIndex(p => p.name === provider.name)}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {icons[provider.name] ? (
                              <img style={{ height: 20, marginRight: 10 }} src={icons[provider.name]} alt={provider.name} />
                            ) : null}
                            {provider.name.toUpperCase()}
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} style={{ marginTop: 10 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8, color: 'var(--text-main)' }}>
                    2. Select Admin Payout Card
                  </Typography>
                  
                  {savedCards.length > 0 ? (
                    <FormControl fullWidth size="small" style={{ marginBottom: 15 }}>
                      <InputLabel style={{ color: 'var(--text-main)' }}>Saved Payout Cards</InputLabel>
                      <Select
                        value={selectedAdminCardId}
                        label="Saved Payout Cards"
                        onChange={(e) => {
                          setSelectedAdminCardId(e.target.value);
                          if (e.target.value !== 'new') {
                            setAdminCardNumber('');
                            setAdminCardHolder('');
                            setAdminCardExpiry('');
                            setAdminCardCvv('');
                          }
                        }}
                        style={{ color: 'var(--text-main)' }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              backgroundColor: 'var(--bg-card)',
                              color: 'var(--text-main)',
                            }
                          }
                        }}
                      >
                        {savedCards.map(item => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.card.network} card ending in {item.card.last4} ({item.card.name})
                          </MenuItem>
                        ))}
                        <MenuItem value="new">+ Use Another Credit / Debit Card</MenuItem>
                      </Select>
                    </FormControl>
                  ) : null}

                  {(savedCards.length === 0 || selectedAdminCardId === 'new') && (
                    <div style={{ border: '1px dashed var(--card-border)', padding: 16, borderRadius: 8 }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 12, color: 'var(--text-main)' }}>
                        Enter Card Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Card Number"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={adminCardNumber}
                            onChange={(e) => setAdminCardNumber(e.target.value)}
                            InputLabelProps={{ style: { color: 'var(--text-main)' } }}
                            inputProps={{ style: { color: 'var(--text-main)' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Card Holder Name"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={adminCardHolder}
                            onChange={(e) => setAdminCardHolder(e.target.value)}
                            InputLabelProps={{ style: { color: 'var(--text-main)' } }}
                            inputProps={{ style: { color: 'var(--text-main)' } }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Expiry (MM/YY)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={adminCardExpiry}
                            onChange={(e) => setAdminCardExpiry(e.target.value)}
                            InputLabelProps={{ style: { color: 'var(--text-main)' } }}
                            inputProps={{ style: { color: 'var(--text-main)' } }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="CVV"
                            type="text"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={adminCardCvv}
                            onChange={(e) => setAdminCardCvv(e.target.value)}
                            InputLabelProps={{ style: { color: 'var(--text-main)' } }}
                            inputProps={{ style: { color: 'var(--text-main)' } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={saveAdminCardCheckbox}
                                onChange={(e) => setSaveAdminCardCheckbox(e.target.checked)}
                                style={{ color: '#E9AE31' }}
                              />
                            }
                            label="Save this card securely for future payouts"
                            style={{ color: 'var(--text-main)' }}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions style={{ borderTop: '1px solid var(--card-border)', paddingTop: 12 }}>
            <Button 
              onClick={() => setPayoutModalOpen(false)} 
              disabled={isProcessing}
              style={{ color: 'var(--text-main)', textTransform: 'none', marginRight: 10 }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleProcessPayout} 
              disabled={isProcessing}
              variant="contained"
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: '#fff', 
                textTransform: 'none',
                padding: '6px 16px',
                fontWeight: 'bold',
                minWidth: 140
              }}
            >
              {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'Process Payout'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Adjust Pool Balance */}
        <Dialog
          open={adjustPoolOpen}
          onClose={() => setAdjustPoolOpen(false)}
          PaperProps={{
            style: {
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-main)',
              border: 'var(--card-border)',
              borderRadius: '16px',
              padding: '16px',
              maxWidth: '450px',
              width: '100%'
            }
          }}
        >
          <DialogTitle style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>
            Adjust Payout Pool
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Override the current liquid balance available for paying out driver withdrawals.
            </Typography>
            <TextField
              label="New Payout Pool Balance"
              variant="outlined"
              size="small"
              fullWidth
              value={newPoolBalance}
              onChange={(e) => setNewPoolBalance(e.target.value)}
              InputLabelProps={{ style: { color: 'var(--text-main)' } }}
              inputProps={{ style: { color: 'var(--text-main)' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdjustPoolOpen(false)} style={{ color: 'var(--text-main)', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAdjustPool} 
              variant="contained"
              sx={{ 
                background: 'linear-gradient(135deg, #E9AE31 0%, #C58F1F 100%)', 
                color: '#000',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F0C35B 0%, #D8A535 100%)',
                }
              }}
            >
              Update Pool
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Collect Cash from Driver */}
        <Dialog
          open={collectionModalOpen}
          onClose={() => setCollectionModalOpen(false)}
          PaperProps={{
            style: {
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-main)',
              border: 'var(--card-border)',
              borderRadius: '16px',
              padding: '16px',
              maxWidth: '480px',
              width: '100%'
            }
          }}
        >
          <DialogTitle style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>
            Collect Cash from Driver
          </DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
            {selectedDriver && (
              <>
                <Typography variant="body1" style={{ color: 'var(--text-main)' }}>
                  Driver: <strong>{selectedDriver.firstName + ' ' + selectedDriver.lastName}</strong>
                </Typography>
                <Typography variant="body1" style={{ color: 'var(--text-main)' }}>
                  Current Wallet Balance: <strong>
                    {settings.swipe_symbol 
                      ? `${selectedDriver.walletBalance} ${settings.symbol}` 
                      : `${settings.symbol} ${selectedDriver.walletBalance}`}
                  </strong> (Owed: {Math.abs(parseFloat(selectedDriver.walletBalance)).toFixed(settings.decimal || 2)})
                </Typography>
                <TextField
                  label="Amount Collected"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={collectedAmount}
                  onChange={(e) => setCollectedAmount(e.target.value)}
                  InputLabelProps={{ style: { color: 'var(--text-main)' } }}
                  inputProps={{ style: { color: 'var(--text-main)' } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={addToPool}
                      onChange={(e) => setAddToPool(e.target.checked)}
                      style={{ color: '#E9AE31' }}
                    />
                  }
                  label="Add collected amount to Admin Payout Pool (Present Amount)"
                  style={{ color: 'var(--text-main)' }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCollectionModalOpen(false)} style={{ color: 'var(--text-main)', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCollectCash} 
              variant="contained"
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: '#fff',
                textTransform: 'none',
                fontWeight: 'bold'
              }}
            >
              Process Collection
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
}

export default Withdraws;
